import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import api from '../../api';
import { usePermissions } from '../../hooks/usePermissions';
import { Layout } from '../../components/layout/Layout';
import { 
  Projector, ArrowLeft, Edit, Trash2,
  Building2, MapPin, Calendar, Tag, 
  Hash, FileText, Package, Monitor, Network
} from 'lucide-react';

const ProyectorDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canUpdate, canDelete } = usePermissions('micro', 'proyectores');
  
  const [proyector, setProyector] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formOptions, setFormOptions] = useState({});
  
  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchProyector();
    fetchFormOptions();
  }, [id]);

  const fetchProyector = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/assets/proyectores/${id}`);
      setProyector(response.data);
    } catch (error) {
      console.error('Error fetching proyector:', error);
      toast.error('Error al cargar el proyector');
      navigate('/micro/proyectores');
    } finally {
      setLoading(false);
    }
  };

  const fetchFormOptions = async () => {
    try {
      const response = await api.get('/assets/proyectores/form/options');
      setFormOptions(response.data);
    } catch (error) {
      console.error('Error fetching form options:', error);
    }
  };

  const openEditModal = () => {
    setFormData({
      empresa_id: proyector.empresa_id || '',
      site_id: proyector.site_id || '',
      brand_id: proyector.brand_id || '',
      asset_tag: proyector.asset_tag || '',
      serial_number: proyector.serial_number || '',
      modelo: proyector.modelo || '',
      ip_address: proyector.ip_address || '',
      ubicacion: proyector.ubicacion || '',
      location_id: proyector.location_id || '',
      status_id: proyector.status_id || '',
      supplier_id: proyector.supplier_id || '',
      resolution: proyector.resolution || '',
      purchase_date: proyector.purchase_date || '',
      comments: proyector.comments || ''
    });
    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    try {
      await api.put(`/assets/proyectores/${id}`, formData);
      toast.success('Proyector actualizado exitosamente');
      setShowEditModal(false);
      fetchProyector();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/assets/proyectores/${id}`);
      toast.success('Proyector eliminado exitosamente');
      navigate('/micro/proyectores');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const getStatusBadge = (status) => {
    if (!status) return <span className="badge badge-default">Sin estado</span>;
    if (status.toLowerCase().includes('activo')) return <span className="badge badge-green">{status}</span>;
    if (status.toLowerCase().includes('inactivo')) return <span className="badge badge-red">{status}</span>;
    return <span className="badge badge-default">{status}</span>;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Cargando...</p>
        </div>
      </Layout>
    );
  }

  if (!proyector) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Proyector no encontrado</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate('/micro/proyectores')}
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" strokeWidth={1.5} />
            </Button>
            <div className="flex items-center gap-3">
              <Projector className="h-8 w-8 text-primary" strokeWidth={1.5} />
              <div>
                <h1 className="text-2xl font-semibold">{proyector.brand_name} {proyector.modelo}</h1>
                <p className="text-sm text-muted-foreground font-mono">{proyector.asset_tag}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canUpdate && (
              <Button variant="outline" onClick={openEditModal} data-testid="edit-btn">
                <Edit className="h-4 w-4 mr-2" strokeWidth={1.5} />
                Editar
              </Button>
            )}
            {canDelete && (
              <Button 
                variant="destructive" 
                onClick={() => setShowDeleteModal(true)}
                data-testid="delete-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" strokeWidth={1.5} />
                Eliminar
              </Button>
            )}
          </div>
        </div>

        {/* Summary Bar */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <div className="nerve-card p-4 text-center">
            <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Estado</div>
            <div>{getStatusBadge(proyector.status_name)}</div>
          </div>
          <div className="nerve-card p-4 text-center">
            <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Marca</div>
            <div className="font-medium">{proyector.brand_name || '-'}</div>
          </div>
          <div className="nerve-card p-4 text-center">
            <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Resolución</div>
            <div className="font-medium">{proyector.resolution || '-'}</div>
          </div>
          <div className="nerve-card p-4 text-center">
            <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">IP</div>
            <div className="font-mono text-sm">{proyector.ip_address || '-'}</div>
          </div>
          <div className="nerve-card p-4 text-center">
            <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Site</div>
            <div className="font-medium">{proyector.site_name || '-'}</div>
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-2 gap-6">
          {/* Identificación */}
          <Card className="card-subtle">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Tag className="h-4 w-4 text-primary" strokeWidth={1.5} />
                Identificación
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Asset Tag</p>
                  <p className="font-mono">{proyector.asset_tag}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Serial</p>
                  <p className="font-mono">{proyector.serial_number || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Marca</p>
                  <p>{proyector.brand_name || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Modelo</p>
                  <p>{proyector.modelo || '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Especificaciones Técnicas */}
          <Card className="card-subtle">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Monitor className="h-4 w-4 text-primary" strokeWidth={1.5} />
                Especificaciones
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Resolución</p>
                  <p>{proyector.resolution || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">IP Address</p>
                  <p className="font-mono">{proyector.ip_address || '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Ubicación y Organización */}
          <Card className="card-subtle">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="h-4 w-4 text-primary" strokeWidth={1.5} />
                Ubicación y Organización
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Empresa</p>
                  <p>{proyector.empresa_name || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Site</p>
                  <p>{proyector.site_name || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Ubicación (FK)</p>
                  <p>{proyector.location_name || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Ubicación (texto)</p>
                  <p>{proyector.ubicacion || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Proveedor</p>
                  <p>{proyector.supplier_name || '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Compra y Notas */}
          <Card className="card-subtle">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-4 w-4 text-primary" strokeWidth={1.5} />
                Compra y Notas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Fecha de Compra</p>
                  <p>{proyector.purchase_date || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Comentarios</p>
                  <p className="text-sm">{proyector.comments || 'Sin comentarios'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Edit Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Projector className="h-5 w-5" strokeWidth={1.5} />
                Editar Proyector
              </DialogTitle>
            </DialogHeader>
            
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label>Asset Tag *</Label>
                <Input
                  value={formData.asset_tag || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, asset_tag: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Serial Number</Label>
                <Input
                  value={formData.serial_number || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, serial_number: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Empresa *</Label>
                <Select value={String(formData.empresa_id || '')} onValueChange={(v) => setFormData(prev => ({ ...prev, empresa_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.empresas?.map(e => (
                      <SelectItem key={e.id} value={String(e.id)}>{e.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Site *</Label>
                <Select value={String(formData.site_id || '')} onValueChange={(v) => setFormData(prev => ({ ...prev, site_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.sites?.map(s => (
                      <SelectItem key={s.id} value={String(s.id)}>{s.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Marca</Label>
                <Select value={String(formData.brand_id || '')} onValueChange={(v) => setFormData(prev => ({ ...prev, brand_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.brands?.map(b => (
                      <SelectItem key={b.id} value={String(b.id)}>{b.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Modelo</Label>
                <Input
                  value={formData.modelo || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, modelo: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Resolución</Label>
                <Input
                  value={formData.resolution || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, resolution: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label>IP Address</Label>
                <Input
                  value={formData.ip_address || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, ip_address: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Estado</Label>
                <Select value={String(formData.status_id || '')} onValueChange={(v) => setFormData(prev => ({ ...prev, status_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.statuses?.map(s => (
                      <SelectItem key={s.id} value={String(s.id)}>{s.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Ubicación (FK)</Label>
                <Select value={String(formData.location_id || '')} onValueChange={(v) => setFormData(prev => ({ ...prev, location_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.ubicaciones?.map(u => (
                      <SelectItem key={u.id} value={String(u.id)}>{u.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Ubicación (texto)</Label>
                <Input
                  value={formData.ubicacion || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, ubicacion: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Proveedor</Label>
                <Select value={String(formData.supplier_id || '')} onValueChange={(v) => setFormData(prev => ({ ...prev, supplier_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.proveedores?.map(p => (
                      <SelectItem key={p.id} value={String(p.id)}>{p.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Fecha de Compra</Label>
                <Input
                  type="date"
                  value={formData.purchase_date || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, purchase_date: e.target.value }))}
                />
              </div>
              
              <div className="col-span-2 space-y-2">
                <Label>Comentarios</Label>
                <Input
                  value={formData.comments || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, comments: e.target.value }))}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleUpdate}>
                Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Modal */}
        <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" strokeWidth={1.5} />
                Eliminar Proyector
              </DialogTitle>
            </DialogHeader>
            <p className="py-4">
              ¿Estás seguro de que deseas eliminar el proyector <strong>{proyector?.brand_name} {proyector?.modelo}</strong> con Asset Tag <strong>{proyector?.asset_tag}</strong>?
              <br />
              <span className="text-sm text-muted-foreground">Esta acción no se puede deshacer.</span>
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ProyectorDetail;
